import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DocumentEditorGate, DocumentProjection, OfficeBootstrapResult } from "@mind/contracts/browser";
import { documentApi } from "./documentApi.js";
import styles from "./DocumentsScreen.module.css";

type OfficeDocumentType = "word" | "cell" | "slide" | "pdf";

type OfficeEditorInstance = {
  readonly destroyEditor?: () => void;
};

type OfficeDocsApi = {
  readonly DocEditor: new (elementId: string, config: Record<string, unknown>) => OfficeEditorInstance;
};

type OfficeWindow = Window & {
  readonly DocsAPI?: OfficeDocsApi;
};

type OfficeEditorEvents = {
  readonly onDocumentReady: () => void;
  readonly onError: () => void;
};

const OFFICE_BOOTSTRAP_TIMEOUT_MS = 10_000;
const OFFICE_SCRIPT_TIMEOUT_MS = 15_000;
const OFFICE_EDITOR_READY_TIMEOUT_MS = 25_000;

const bootstrapPromises = new Map<string, Promise<OfficeBootstrapResult>>();
const scriptPromises = new Map<string, Promise<void>>();

function bootstrapOffice(officeOrigin: string, token: string): Promise<OfficeBootstrapResult> {
  const cacheKey = `${officeOrigin}\u0000${token}`;
  const cached = bootstrapPromises.get(cacheKey);
  if (cached !== undefined) return cached;
  const promise = documentApi.bootstrapOffice(officeOrigin, token, AbortSignal.timeout(OFFICE_BOOTSTRAP_TIMEOUT_MS));
  bootstrapPromises.set(cacheKey, promise);
  void promise.then((result) => {
    const expiresIn = Math.max(Date.parse(result.expiresAt) - Date.now(), 1_000);
    window.setTimeout(() => {
      if (bootstrapPromises.get(cacheKey) === promise) bootstrapPromises.delete(cacheKey);
    }, expiresIn);
  }, () => {
    if (bootstrapPromises.get(cacheKey) === promise) bootstrapPromises.delete(cacheKey);
  });
  return promise;
}

function officeScript(officeOrigin: string, signal: AbortSignal): Promise<void> {
  if ((window as OfficeWindow).DocsAPI !== undefined) return Promise.resolve();

  const source = `${officeOrigin.replace(/\/$/, "")}/web-apps/apps/api/documents/api.js`;
  const cached = scriptPromises.get(source);
  const promise = cached ?? new Promise<void>((resolve, reject) => {
    let script: HTMLScriptElement | null = null;
    for (const candidate of document.scripts) {
      if (candidate.src === source) {
        script = candidate;
        break;
      }
    }
    if (script === null) {
      script = document.createElement("script");
      script.src = source;
      script.async = true;
    }

    let settled = false;
    let timeoutId: number | undefined;
    let pollId: number | undefined;
    const cleanup = () => {
      script.removeEventListener("load", loaded);
      script.removeEventListener("error", failed);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      if (pollId !== undefined) window.clearInterval(pollId);
    };
    const settle = (failure?: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      if (failure === undefined) {
        resolve();
        return;
      }
      if ((window as OfficeWindow).DocsAPI === undefined) script.remove();
      reject(failure);
    };
    const ready = () => {
      if ((window as OfficeWindow).DocsAPI !== undefined) settle();
    };
    const loaded = () => {
      window.setTimeout(() => {
        if ((window as OfficeWindow).DocsAPI === undefined) {
          settle(new Error("Office editor script did not initialize"));
          return;
        }
        settle();
      }, 0);
    };
    const failed = () => settle(new Error("Office editor script failed to load"));

    script.addEventListener("load", loaded, { once: true });
    script.addEventListener("error", failed, { once: true });
    timeoutId = window.setTimeout(() => settle(new Error("Office editor script timed out")), OFFICE_SCRIPT_TIMEOUT_MS);
    pollId = window.setInterval(ready, 100);
    ready();
    if (!script.isConnected) document.head.append(script);
  });

  if (cached === undefined) {
    scriptPromises.set(source, promise);
    void promise.then(
      () => {
        if (scriptPromises.get(source) === promise) scriptPromises.delete(source);
      },
      () => {
        if (scriptPromises.get(source) === promise) scriptPromises.delete(source);
      },
    );
  }

  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("The editor load was cancelled", "AbortError"));
      return;
    }
    const abort = () => reject(new DOMException("The editor load was cancelled", "AbortError"));
    signal.addEventListener("abort", abort, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener("abort", abort));
  });
}

function officeDocumentType(document: Pick<DocumentProjection, "displayName" | "mediaType">): OfficeDocumentType {
  const extension = document.displayName.toLowerCase().split(".").pop();
  if (extension === "pdf" || document.mediaType === "application/pdf") return "pdf";
  if (["xls", "xlsx", "ods", "csv"].includes(extension ?? "")) return "cell";
  if (["ppt", "pptx", "odp"].includes(extension ?? "")) return "slide";
  return "word";
}

function officeEditorConfig(gate: DocumentEditorGate, fileGrant: string, editorToken: string, locale: string, events: OfficeEditorEvents): Record<string, unknown> {
  const appOrigin = window.location.origin;
  const documentType = officeDocumentType(gate.document);
  const extension = gate.document.displayName.toLowerCase().split(".").pop();
  return {
    documentType,
    type: "desktop",
    height: "100%",
    width: "100%",
    token: editorToken,
    document: {
      fileType: extension === undefined || extension === gate.document.displayName.toLowerCase() ? documentType : extension,
      key: gate.editorKey,
      title: gate.document.displayName,
      url: `${appOrigin}/api/office/files/${encodeURIComponent(fileGrant)}`,
    },
    editorConfig: {
      callbackUrl: `${appOrigin}/api/office/callback`,
      mode: gate.mode,
      lang: locale.startsWith("ru") ? "ru-RU" : "en-US",
      user: {
        id: gate.editSessionId,
        name: "Secure Mind",
      },
      customization: {
        compactHeader: true,
        compactToolbar: true,
        hideRightMenu: false,
      },
    },
    events,
  };
}

export function OfficeEditor({
  gate,
  onClose,
  onRetry,
  closing = false,
  closeFailed = false,
  retrying = false,
}: {
  readonly gate: DocumentEditorGate;
  readonly onClose: () => void;
  readonly onRetry: () => void;
  readonly closing?: boolean;
  readonly closeFailed?: boolean;
  readonly retrying?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<OfficeEditorInstance | null>(null);
  const loadControllerRef = useRef<AbortController | null>(null);
  const closeRequestedRef = useRef(false);
  const [state, setState] = useState<"loading" | "ready" | "error" | "close_error">("loading");
  const hostId = `office-editor-${gate.editSessionId}`;
  const requestClose = () => {
    if (closeRequestedRef.current) return;
    closeRequestedRef.current = true;
    loadControllerRef.current?.abort();
    editorRef.current?.destroyEditor?.();
    editorRef.current = null;
    onClose();
  };

  useEffect(() => {
    const controller = new AbortController();
    loadControllerRef.current = controller;
    closeRequestedRef.current = false;
    let disposed = false;
    let editorFailed = false;
    setState("loading");
    sectionRef.current?.focus();
    const start = async () => {
      try {
        if (gate.bootstrapToken === null) throw new Error("Office bootstrap authorization is unavailable");
        const bootstrap = await bootstrapOffice(gate.officeOrigin, gate.bootstrapToken);
        if (disposed || controller.signal.aborted) return;
        await officeScript(gate.officeOrigin, controller.signal);
        if (disposed || controller.signal.aborted || hostRef.current === null) return;
        const docsApi = (window as OfficeWindow).DocsAPI;
        if (docsApi === undefined) throw new Error("Office editor API is unavailable");

        await new Promise<void>((resolve, reject) => {
          let settled = false;
          let documentReady = false;
          const finish = (failure?: Error) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(readyTimeout);
            controller.signal.removeEventListener("abort", abort);
            if (failure === undefined) resolve();
            else reject(failure);
          };
          const abort = () => finish(new DOMException("The editor load was cancelled", "AbortError"));
          const readyTimeout = window.setTimeout(
            () => finish(new Error("Office editor did not become ready")),
            OFFICE_EDITOR_READY_TIMEOUT_MS,
          );
          controller.signal.addEventListener("abort", abort, { once: true });
          const events: OfficeEditorEvents = {
            onDocumentReady: () => {
              if (disposed) return;
              documentReady = true;
              hostRef.current?.querySelector<HTMLIFrameElement>("iframe")?.focus();
              finish();
            },
            onError: () => {
              if (disposed) return;
              editorFailed = true;
              if (!documentReady) {
                finish(new Error("Office editor reported an error"));
                return;
              }
              editorRef.current?.destroyEditor?.();
              editorRef.current = null;
              setState("error");
            },
          };
          try {
            const editor = new docsApi.DocEditor(hostId, officeEditorConfig(gate, bootstrap.fileGrant, bootstrap.editorToken, i18n.language, events));
            editorRef.current = editor;
            if (editorFailed) {
              editor.destroyEditor?.();
              editorRef.current = null;
            }
          } catch (caught) {
            finish(caught instanceof Error ? caught : new Error("Office editor construction failed"));
          }
        });
        if (editorFailed) throw new Error("Office editor reported an error");
        if (!disposed && !controller.signal.aborted) setState("ready");
      } catch {
        if (disposed || controller.signal.aborted) return;
        editorRef.current?.destroyEditor?.();
        editorRef.current = null;
        setState("error");
      }
    };
    void start();

    return () => {
      disposed = true;
      controller.abort();
      if (loadControllerRef.current === controller) loadControllerRef.current = null;
      editorRef.current?.destroyEditor?.();
      editorRef.current = null;
    };
  }, [gate, hostId, i18n.language]);

  useEffect(() => {
    if (!closeFailed) return;
    closeRequestedRef.current = false;
    setState("close_error");
  }, [closeFailed]);

  return (
    <section ref={sectionRef} className={styles.officeEditor} aria-label={t("documents.officeLifecycle")} tabIndex={-1}>
      <header className={styles.officeEditorHeader}>
        <div>
          <span className={styles.eyebrow}>{t("documents.officeLifecycle")}</span>
          <h2>{gate.document.displayName}</h2>
        </div>
        <div className={styles.headerActions}>
          <span className={styles.statusBadge} data-state={closing ? "closing" : state} role="status">{closing ? t("documents.officeFinalizing") : state === "loading" ? t("documents.officeEditorLoading") : state === "ready" ? t("documents.officeEditorReady") : state === "close_error" ? t("documents.officeFinalizeError") : t("documents.officeEditorError")}</span>
          <button type="button" className={styles.textButton} onClick={requestClose} disabled={closing}>{t("documents.close")}</button>
        </div>
      </header>
      {state === "error" || state === "close_error" ? (
        <>
          <p className={styles.officeEditorError} role="alert">{t(state === "close_error" ? "documents.officeFinalizeRetryAction" : "documents.officeEditorErrorAction")}</p>
          <button type="button" className={styles.textButton} onClick={state === "close_error" ? requestClose : onRetry} disabled={retrying || closing}>{t("common.retry")}</button>
        </>
      ) : null}
      <div id={hostId} ref={hostRef} className={styles.officeEditorHost} aria-busy={state === "loading"} hidden={state === "error" || state === "close_error"} />
    </section>
  );
}
